import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { useAuth } from "@/hooks/use-auth";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

// Pages
import Register from "@/pages/auth/Register";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import VerifyPhone from "@/pages/auth/VerifyPhone";
import Dashboard from "@/pages/Dashboard";
import Forms from "@/pages/Forms";
import FormDetail from "@/pages/FormDetail";
import FormBuilder from "@/pages/admin/FormBuilder";
import UserManagement from "@/pages/admin/UserManagement";
import DistrictManagement from "@/pages/admin/DistrictManagement";
import AddUser from "@/pages/admin/AddUser";
import SubmissionReview from "@/pages/SubmissionReview";
import VisitorAssignments from "@/pages/VisitorAssignments";
import Profile from "@/pages/Profile";
import NotFound from "@/pages/NotFound";
import DashboardLayout from "./components/layout/DashboardLayout";
import AuthLayout from "./components/layout/AuthLayout";
import Login from "./pages/auth/Login";
import VerifyAccount from "./pages/auth/VerifyAccount";
import RequestVerificationPage from "./pages/auth/RequestVerificationPage";
import FormsList from "./components/admin/FormsList";
import FormView from "./components/admin/FormView";
import NewLoomForm from "./pages/forms/NewLoomForm";
import ApplicationDetails from "./pages/forms/ApplicationDetails";
import Auth from "./pages/auth/Auth";
import RequestTwoVerificationPage from "./pages/auth/RequestTwoVerificationPage";
import ForgotPassword from "./pages/auth/ForgotPassword";
import CommonFacilityCentreForm from "./pages/forms/CommonFacilityCentreForm";
import OldConventionalForm from "./pages/forms/OldConventionalForm";
import AirJetLooms from "./pages/forms/AirJetLooms";
import ReferenceDocuments from "./pages/ReferenceDocuments";
import TermsAndConditions from "./pages/TermsAndConditions";
import HowToApply from "./pages/HowToApply";
import DistrictOfficerManagement from "./pages/admin/DistrictOfficerManagement";
import ReportManagement from "./pages/admin/ReportManagement";

const App = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Routes>
        <Route element={<AuthLayout />}>
          <Route
            path="/Auth"
            element={!user ? <Auth /> : <Navigate to="/dashboard" />}
          />
          <Route
            path="/login"
            element={!user ? <Login /> : <Navigate to="/dashboard" />}
          />
          <Route
            path="/register"
            element={!user ? <Register /> : <Navigate to="/dashboard" />}
          />
          <Route
            path="/forgot-password"
            element={!user ? <ForgotPassword /> : <Navigate to="/login" />}
          />
          <Route
            path="/verify-email"
            element={
              !user?.email_verified_at ? (
                <VerifyEmail />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/verify-phone"
            element={
              user?.email_verified_at && !user?.phone_verified_at ? (
                <VerifyPhone />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route path="/verify-account" element={<VerifyAccount />} />
          <Route
            path="/request-verification"
            element={<RequestVerificationPage />}
          />
          <Route
            path="/request-two-verification"
            element={<RequestTwoVerificationPage />}
          />
        </Route>

        {user && (
          <Route element={<DashboardLayout />}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="scheme" element={<Forms />} />
            <Route
              path="reference_documents"
              element={<ReferenceDocuments />}
            />
            <Route
              path="terms_and_condition"
              element={<TermsAndConditions />}
            />
            <Route path="how_to_apply" element={<HowToApply />} />
            <Route
              path="scheme/modernization/:id"
              element={<OldConventionalForm />}
            />
            <Route
              path="scheme/new_shuttleless/:id"
              element={<NewLoomForm />}
            />
            <Route
              path="scheme/new_airjetlooms/:id"
              element={<AirJetLooms />}
            />
            <Route
              path="scheme/cfc/:id"
              element={<CommonFacilityCentreForm />}
            />
            <Route
              path="scheme/modernization"
              element={<OldConventionalForm />}
            />
            <Route path="scheme/new_shuttleless" element={<NewLoomForm />} />
            <Route path="scheme/new_airjetlooms" element={<AirJetLooms />} />
            <Route path="scheme/cfc" element={<CommonFacilityCentreForm />} />
            <Route
              path="application/:type/:id"
              element={<ApplicationDetails />}
            />
            <Route path="forms/:id" element={<FormDetail />} />
            <Route path="profile" element={<Profile />} />

            {user.role === "admin" && (
              <>
                <Route path="admin/forms" element={<FormsList />} />
                <Route path="admin/forms/create" element={<FormBuilder />} />
                <Route path="admin/forms/:id/edit" element={<FormBuilder />} />
                <Route path="admin/forms/:id" element={<FormView />} />
                <Route
                  path="admin/applicant_users"
                  element={<UserManagement />}
                />
                <Route
                  path="admin/district_users"
                  element={<DistrictOfficerManagement />}
                />
                <Route
                  path="admin/report"
                  element={<ReportManagement />}
                />
                <Route path="admin/users/add" element={<AddUser />} />
                <Route
                  path="admin/districts"
                  element={<DistrictManagement />}
                />
                <Route path="submissions" element={<SubmissionReview />} />
              </>
            )}

            {user.role === "district_admin" && (
              <>
                <Route path="submissions" element={<SubmissionReview />} />
              </>
            )}

            {user.role === "district_visitor" && (
              <>
                <Route path="assignments" element={<VisitorAssignments />} />
              </>
            )}
          </Route>
        )}

        {/* Root redirect */}
        <Route
          path="/"
          element={
            user ? <Navigate to="/dashboard" /> : <Navigate to="/Auth" />
          }
        />

        {/* Fallback route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Toaster />
    </div>
  );
};

export default App;
